import React, { useState, useEffect } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import RegistrationClosed from "./registrationClosed";
import Wrapper from "../wrapper";
import Line from "../../assets/images/Line.svg";
import { subscribeToSiteSettings } from "../../lib/firestoreContent";

function CourseSelection() {
  const navigate = useNavigate();
  const [loadingSettings, setLoadingSettings] = useState(true);
  const [isRegistrationOpen, setIsRegistrationOpen] = useState(true);

  const [formData, setFormData] = useState(() => {
    const saved = localStorage.getItem("courseFormData");
    if (saved) {
      try {
        return JSON.parse(saved);
      } catch (e) {
        return {};
      }
    }
    return {
      preferredLearningTrack: "",
      experienceLevel: "",
      availability: "",
      requirements: "",
      motivationForJoining: "",
      additionalInformation: "",
    };
  });

  const [validationError, setValidationError] = useState("");

  useEffect(() => {
    const unsubscribe = subscribeToSiteSettings((settings) => {
      setIsRegistrationOpen(!!settings?.registrationOpen);
      setLoadingSettings(false);
    });
    return () => unsubscribe && unsubscribe();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setValidationError("");
  };

  const wordCount = (str = "") => {
    return str ? str.trim().split(/\s+/).filter(Boolean).length : 0;
  };

  const handleNext = (e) => {
    if (e) e.preventDefault();
    if (
      !formData.preferredLearningTrack ||
      !formData.experienceLevel ||
      !formData.availability ||
      !formData.motivationForJoining
    ) {
      setValidationError("Please fill out all required fields marked with *");
      return;
    }
    localStorage.setItem("courseFormData", JSON.stringify(formData));
    navigate("/submit");
  };

  if (loadingSettings) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <p className="text-slate-500">Checking registration status…</p>
      </div>
    );
  }

  if (!isRegistrationOpen) {
    return <RegistrationClosed />;
  }

  return (
    <div style={{ fontFamily: "'Clash Display', sans-serif" }}>
      <Wrapper>
        <h1
          style={{ fontFamily: "'Clash Display', sans-serif", fontWeight: 900 }}
          className="text-center mt-[150px] m-5 text-[30px] md:text-[35px] text-primary font-bolder"
        >
          Let’s get you registered
        </h1>
        <p
          style={{ fontFamily: "'Clash Display', sans-serif", fontWeight: 900 }}
          className="text-center m-5 text-[22px] md:text-[25px] text-[#4B4B4B] font-bolder"
        >
          Select your course and learning path
        </p>
        <nav>
          <ul
            style={{
              fontFamily: "'Clash Display', sans-serif",
              fontWeight: 500,
            }}
            className="flex w-[90%] m-auto justify-between text-[14px]"
          >
            <li>
              <NavLink
                to="/register"
                className={({ isActive }) =>
                  `text-lg ${isActive ? "font-bold text-primary underline" : ""}`
                }
              >
                General Details
              </NavLink>
            </li>
            <li>
              <NavLink
                to="/course-selection"
                className={({ isActive }) =>
                  `text-lg ${isActive ? "font-bold text-primary underline" : ""}`
                }
              >
                Course Selection
              </NavLink>
            </li>
            <li>
              <NavLink
                to="/submit"
                className={({ isActive }) =>
                  `text-lg ${isActive ? "font-bold text-primary underline" : ""}`
                }
              >
                Submit
              </NavLink>
            </li>
          </ul>
        </nav>
        <img src={Line} alt="" className="w-[90%] m-auto h-[20px]" />

        {validationError && (
          <p role="alert" className="w-[90%] md:w-[70%] m-auto mt-4 rounded-md bg-red-50 p-3 text-sm text-red-600">
            {validationError}
          </p>
        )}

        <form
          className="bg-white rounded-md shadow-2xl w-[90%] md:w-[70%] m-auto p-[30px] mt-[50px] mb-[30px]"
          onSubmit={handleNext}
        >
          <div className="md:flex">
            <div className="w-[90%] m-auto mt-[10px]">
              <label className="font-medium text-[17px]">
                Learning Track<span className="text-[red] ml-1">*</span>
              </label>
              <br />
              <select
                name="preferredLearningTrack"
                value={formData.preferredLearningTrack || ""}
                onChange={handleChange}
                className="border border-black rounded-md mt-[5px] p-[4px] w-[90%]"
                required
              >
                <option value="">Select your learning track</option>
                <option value="Robotics & Arduino">Robotics & Arduino</option>
                <option value="Data Science">Data Science</option>
                <option value="UI/UX Design">UI/UX Design</option>
                <option value="Frontend Web Development">Frontend Web Development</option>
                <option value="Backend Development">Backend Development</option>
                <option value="Scratch">Scratch</option>
                <option value="C++">C++</option>
              </select>
              <p className="text-[#7B7A7A] text-[14px]">
                NB: Each person can only register for one learning track
              </p>
            </div>
            <div className="w-[90%] m-auto mt-[10px]">
              <label className="font-medium text-[17px]">
                Experience Level<span className="text-[red] ml-1">*</span>
              </label>
              <br />
              <select
                name="experienceLevel"
                value={formData.experienceLevel || ""}
                onChange={handleChange}
                className="border border-black rounded-md mt-[5px] p-[4px] w-[90%]"
                required
              >
                <option value="">Select your experience level</option>
                <option value="Beginner">Beginner</option>
                <option value="Intermediate">Intermediate</option>
                <option value="Advanced">Advanced</option>
              </select>
              <p className="text-[#7B7A7A] text-[14px]">
                Choose your experience level in the selected learning track
              </p>
            </div>
          </div>

          <div className="md:flex">
            <div className="w-[90%] m-auto mt-[10px]">
              <label className="font-medium text-[17px]">
                Availability<span className="text-[red] ml-1">*</span>
              </label>
              <br />
              <select
                name="availability"
                value={formData.availability || ""}
                onChange={handleChange}
                className="border border-black rounded-md mt-[5px] p-[4px] w-[90%]"
                required
              >
                <option value="">Select your availability</option>
                <option value="Yes">Yes, I can dedicate 8+ hrs/week</option>
                <option value="No">No</option>
              </select>
              <p className="text-[#7B7A7A] text-[14px]">
                Can you dedicate at least 8hrs/week for the duration of the bootcamp?
              </p>
            </div>
            <div className="w-[90%] m-auto mt-[10px]">
              <label className="font-medium text-[17px]">
                Computer / Device Access<span className="text-[red] ml-1">*</span>
              </label>
              <br />
              <div className="flex space-x-4 mt-[5px]">
                <label className="flex items-center">
                  <input
                    type="radio"
                    name="requirements"
                    value="computer"
                    checked={formData.requirements === "computer"}
                    onChange={handleChange}
                    className="mr-2"
                  />
                  Laptop / PC
                </label>
                <label className="flex items-center">
                  <input
                    type="radio"
                    name="requirements"
                    value="any"
                    checked={formData.requirements === "any"}
                    onChange={handleChange}
                    className="mr-2"
                  />
                  Tablet / Phone only
                </label>
              </div>
            </div>
          </div>

          <div className="md:flex mt-4">
            <div className="w-[90%] m-auto mt-[10px]">
              <label className="font-medium text-[17px]">
                Motivation for Joining<span className="text-[red] ml-1">*</span>
              </label>
              <br />
              <textarea
                name="motivationForJoining"
                value={formData.motivationForJoining || ""}
                onChange={handleChange}
                className="border border-black rounded-md mt-[5px] p-[4px] w-[90%]"
                placeholder="Why do you want to join and what do you hope to achieve?"
                maxLength="2500"
                rows="4"
                required
              />
              <p className="text-[#7B7A7A] text-[14px]">
                {wordCount(formData.motivationForJoining)} words
              </p>
            </div>
            <div className="w-[90%] m-auto mt-[10px]">
              <label className="font-medium text-[17px]">
                Additional Comments or Questions
              </label>
              <br />
              <textarea
                name="additionalInformation"
                value={formData.additionalInformation || ""}
                onChange={handleChange}
                className="border border-black rounded-md mt-[5px] p-[4px] w-[90%]"
                placeholder="Any questions or extra information for the team"
                maxLength="2500"
                rows="4"
              />
            </div>
          </div>

          <div className="flex justify-between w-full mt-6">
            <NavLink
              to="/register"
              className="p-[10px] rounded-md border border-slate-300 text-slate-700 hover:bg-slate-50 transition"
            >
              Previous
            </NavLink>
            <button
              type="submit"
              className="p-[10px] rounded-md bg-primary text-white hover:bg-[#1b1449] transition font-medium"
            >
              Save & Continue
            </button>
          </div>
        </form>
      </Wrapper>
    </div>
  );
}

export default CourseSelection;
